/**
 * What a webview handed over when it was submitted, kept on the run — the
 * values the form passed to the bot (`PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH`),
 * flattened into named fields a person can read and a judge can check.
 *
 * Runs are saved in the browser and sent to the scoring model, so a value
 * that could be a card, an account or a secret is masked here, before it is
 * kept anywhere: by its field name, and by its shape (a long run of digits).
 * Only the last four digits survive, which is enough to tell test values apart.
 */

export interface SubmittedField {
  /** Dotted path into the data: `incident-time.value`, `card.number`. */
  name: string;
  value: string;
  /** Replaced with a mask — the run never held the real value. */
  masked?: boolean;
  /**
   * `value` was cut short or summarized ("(2 items)") for reading. `replay`
   * then holds what the form really sent, so a case saved from the run can
   * send it again — kept only when it is not too long and nothing in it
   * would have been masked.
   */
  shortened?: boolean;
  replay?: string;
}

export interface WebviewSubmission {
  /** The webview's name, as the bot labelled it. */
  form: string;
  fields: SubmittedField[];
}

const MAX_FIELDS = 30;
const MAX_VALUE = 200;
const MAX_REPLAY = 4000;

/**
 * A field whose own name says it holds a card, security or account value.
 * Read from the field's last name only, so `cardInfo.cardName` and a lone
 * `cardMonth` stay readable: masking them made the judge think the bot
 * invented the name and expiry it read back.
 */
const SENSITIVE_NAME =
  /card.?(num|no)|cc.?(num|no)|cvv|cvc|ccv|security.?code|exp(iry|iration)?.?date|ssn|social|tax.?id|account|acct|routing|iban|swift|sort.?code|^pin$|passw|passcode|secret|token|otp|dob|birth/i;

const mask = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : '••••';
};

/** A run of 9+ digits (spaces and dashes allowed) reads as a card or account. */
const looksLikeNumber = (value: string) =>
  /^[\d\s-]+$/.test(value) && value.replace(/\D/g, '').length >= 9;

/**
 * A value that is itself JSON, as a short note: the address form sends
 * Google's whole address breakdown as one string, which buried the address.
 */
const structuredNote = (value: string): string | null => {
  const t = value.trim();
  if (!/^[[{]/.test(t)) return null;
  try {
    const parsed: unknown = JSON.parse(t);
    if (Array.isArray(parsed)) return `(${parsed.length} items)`;
    if (parsed && typeof parsed === 'object') return '(structured data)';
  } catch {
    // Not JSON after all; shown as text.
  }
  return null;
};

/** Whether a value that is itself JSON holds anything that would be masked. */
const holdsSensitive = (json: string): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return flattenSubmission(JSON.parse(json)).some((f) => f.masked);
  } catch {
    return false;
  }
};

const leaf = (name: string, raw: unknown): SubmittedField => {
  const full = String(raw ?? '');
  const value = full.slice(0, MAX_VALUE);
  const last = name.split('.').pop() ?? name;
  if (SENSITIVE_NAME.test(last) || looksLikeNumber(value)) {
    return { name, value: mask(value), masked: true };
  }
  const note = structuredNote(full);
  if (note === null && value === full) return { name, value };
  const keep =
    full.length <= MAX_REPLAY && (note === null || !holdsSensitive(full));
  return {
    name,
    value: note ?? value,
    shortened: true,
    ...(keep ? { replay: full } : {}),
  };
};

/**
 * The submitted data as fields. Arrays of one object (the usual shape —
 * `[{ "incident-time": { … } }]`) don't add an index to every name; a
 * `{ name, value }` pair is read as one field.
 */
export const flattenSubmission = (data: unknown): SubmittedField[] => {
  const fields: SubmittedField[] = [];
  const isPair = (v: unknown): v is { name: string; value: unknown } =>
    !!v &&
    typeof v === 'object' &&
    typeof (v as { name?: unknown }).name === 'string' &&
    'value' in v &&
    Object.keys(v).length <= 2;
  const walk = (value: unknown, path: string) => {
    if (fields.length >= MAX_FIELDS) return;
    if (Array.isArray(value)) {
      // One item, or named pairs, already say which field is which.
      if (value.length === 1 || value.every(isPair)) {
        value.forEach((v) => walk(v, path));
      } else {
        value.forEach((v, i) =>
          walk(v, path ? `${path}.${i + 1}` : String(i + 1))
        );
      }
      return;
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if (isPair(record)) {
        walk(record.value, path ? `${path}.${record.name}` : record.name);
        return;
      }
      Object.entries(record).forEach(([k, v]) =>
        walk(v, path ? `${path}.${k}` : k)
      );
      return;
    }
    fields.push(leaf(path || 'value', value));
  };
  walk(data, '');
  return fields;
};

/** Merge more data into what a form already handed over (a field sent twice keeps the last). */
export const addSubmission = (
  prev: WebviewSubmission | null,
  form: string,
  data: unknown
): WebviewSubmission => {
  const byName = new Map((prev?.fields ?? []).map((f) => [f.name, f]));
  flattenSubmission(data).forEach((f) => byName.set(f.name, f));
  return { form, fields: [...byName.values()].slice(0, MAX_FIELDS) };
};

/** One line for the judge: `Submitted Select a Time: incident-time.value = 13:30, …`. */
export const describeSubmission = (s: WebviewSubmission): string =>
  `Submitted ${s.form}: ${
    s.fields.length
      ? s.fields.map((f) => `${f.name} = ${f.value}`).join(', ')
      : 'no fields'
  }`;
